import 'C:\\Projects\\genkei\\zumeco\\static\\shared\\components\\ContactListDialog.tsx.esbuild-plugin-linaria.css';
import 'C:\\Projects\\genkei\\zumeco\\static\\shared\\components\\ContactListDialog.tsx.esbuild-plugin-windicss.css';
import { __ACCOUNTS_URL_BASE__ } from '@@urls';
import { css } from '@linaria/core';
import { mdiAccountPlusOutline, mdiDeleteOutline, mdiEmailOutline } from '@mdi/js';
import { RefObject } from 'preact';
import { createSharedSelector, createSharedState } from 'preact-shared-state-hook';
import { forwardRef, memo } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { DoodlePermissions } from '../../../server/src/models';
import { generateId } from '../../doodle/generateId';
import { Panel } from '../../doodle/view/Panel';
import { spreadsheetEnabled } from '../../doodle/view/Spreadsheet';
import { COLOR_DANGER } from '../colors';
import { i18n } from '../i18n';
import { isNonNull, preventDefault, withDefaultErrorHandler, withProgressBar } from '../utils';
import { DefaultButton } from './DefaultButton';
import { Dialog, showModal } from './Dialog';
import { PrimaryButton } from './PrimaryButton';
import { SubmitButton } from './SubmitButton';
interface Contact {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly sortKey: string;
}
const urlContacts = (contactId?: string) => `${__ACCOUNTS_URL_BASE__}/api/current-user/contacts${contactId ? `/${contactId}` : ''}`;
export const compareContacts = (a: Contact, b: Contact) => a.sortKey.localeCompare(b.sortKey) || a.name.localeCompare(b.name) || a.email.localeCompare(b.email);
const [useContactMap, setContactMap] = createSharedState<{ [id in string]: Contact }>();
const useContacts = createSharedSelector([useContactMap], map => map && Object.values(map).sort(compareContacts));
const loadContacts = () => fetch(urlContacts(), {
  mode: 'cors',
  credentials: 'include'
}).then(async response => {
  if (response.ok) {
    setContactMap(((await response.json()) as {
      contacts: { [id in string]: Contact };
    }).contacts);
  } else {
    alert(i18n.contacts.loadError(response));
  }
});
const saveContact = ({
  id,
  ...body
}: Contact) => fetch(urlContacts(id), {
  method: 'PUT',
  headers: {
    'Content-Type': 'application/json; charset=utf-8'
  },
  mode: 'cors',
  credentials: 'include',
  body: JSON.stringify(body)
});
const deleteContacts = async (ids: readonly string[]) => {
  await withProgressBar(async () => {
    for (const id of ids) {
      if (await withDefaultErrorHandler(fetch(urlContacts(id), {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include'
      }))) {
        setContactMap(contactMap => {
          contactMap = {
            ...contactMap
          };
          delete contactMap[id];
          return contactMap;
        });
      } else {
        break;
      }
    }
  });
};
const ContactEditForm = memo(({
  source,
  close
}: {
  source?: Partial<Contact>;
  close: () => unknown;
}) => {
  const [submitting, setSubmitting] = useState<Contact>();
  return <form class="flex flex-col gap-4" onSubmit={event => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement & Record<'email' | 'name' | 'sortKey', HTMLInputElement>;
    const contact = {
      id: source?.id ?? generateId(),
      email: form.email.value.trim(),
      name: form.name.value.trim(),
      sortKey: form.sortKey.value.trim()
    };
    const existingSameEmail = useContacts.snapshot()?.find(c => c.id !== contact.id && c.email === contact.email);
    if (existingSameEmail && !confirm(i18n.contacts.sameEmailExists(contact, existingSameEmail))) {
      return;
    }
    void withProgressBar(async () => {
      setSubmitting(contact);
      try {
        if (await withDefaultErrorHandler(saveContact(contact))) {
          setContactMap(recent => ({
            ...recent,
            [contact.id]: contact
          }));
          close();
        }
      } finally {
        setSubmitting(undefined);
      }
    });
  }}>
      <label>
        <div>{i18n.contacts.name}</div>
        <input class="mt-1 border-primary rounded" name="name" value={submitting?.name ?? source?.name ?? ''} required />
      </label>
      <label>
        <div>{i18n.contacts.sortKey}</div>
        <input class="mt-1 border-primary rounded" name="sortKey" value={submitting?.sortKey ?? source?.sortKey ?? ''} required />
      </label>
      <label>
        <div>{i18n.contacts.email}</div>
        <input class="mt-1 border-primary rounded" name="email" value={submitting?.email ?? source?.email ?? ''} required />
      </label>
      <div class="flex justify-end">
        <SubmitButton label={i18n.save} disabled={!!submitting} />
        <DefaultButton label={i18n.cancel} onClick={close} />
      </div>
    </form>;
});
const Checkbox = ({
  name,
  label
}: {
  name: string;
  label: string;
}) => <label class="cursor-pointer flex items-center gap-2 py-1">
    <input type="checkbox" name={name} />
    <div>{label}</div>
  </label>;
const ConfirmationForm = memo(({
  currentUser,
  contacts,
  onSubmit,
  onCancel
}: {
  currentUser: {
    readonly email?: string;
  };
  contacts: readonly Contact[];
  onSubmit: (permissions: DoodlePermissions) => Promise<unknown>;
  onCancel: () => unknown;
}) => {
  const [submitting, setSubmitting] = useState(false);
  return <form
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  onSubmit={async event => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement & Record<'edit' | 'output' | 'spreadsheetEdit', HTMLInputElement>;
    setSubmitting(true);
    try {
      await onSubmit({
        edit: form.edit.checked ? 1 : undefined,
        output: form.output.checked ? 1 : undefined,
        spreadsheetEdit: form.spreadsheetEdit?.checked ? 1 : undefined
      });
    } finally {
      setSubmitting(false);
    }
  }}>
        <p class="whitespace-pre-wrap">{i18n.contacts.confirmationDescription(contacts.length)}</p>
        <ul class="mt-3 list-disc leading-tight">
          {contacts.map(contact => <li key={contact.id}>{contact.name}</li>)}
        </ul>
        <div class="mt-3">
          <Checkbox name="edit" label={i18n.sharing.editPermission} />
          <Checkbox name="output" label={i18n.sharing.outputPermission} />
          {spreadsheetEnabled(currentUser) && <Checkbox name="spreadsheetEdit" label={i18n.sharing.spreadsheetEditPermissionLong} />}
        </div>
        <div class="mt-3 flex justify-end">
          <SubmitButton disabled={submitting} label={i18n.sharing.invite} />
          <DefaultButton disabled={submitting} label={i18n.cancel} onClick={onCancel} />
        </div>
      </form>;
});
const collectSelectedContactIds = () => {
  const contactMap = useContactMap.snapshot();
  return [...document.querySelectorAll('.fb4af615:checked:not(:disabled)')].map(checkbox => {
    const id = checkbox.closest('[data-id]')?.getAttribute('data-id');
    return id ? contactMap?.[id] : undefined;
  }).filter(isNonNull).sort(compareContacts);
};
const ContactList = memo(({
  disabled,
  guests,
  startEditing,
  setSelectedContacts
}: {
  disabled?: boolean;
  guests: readonly {
    readonly email: string;
  }[];
  startEditing: (contact: Contact) => void;
  setSelectedContacts: (contacts: readonly Contact[]) => void;
}) => {
  const contacts = useContacts();
  const onChangeChecked = useCallback(() => setSelectedContacts(collectSelectedContactIds()), [setSelectedContacts]);
  const onClickEditButton = useCallback((event: Event) => {
    const id = (event.target as Element).closest?.('[data-id]')?.getAttribute('data-id');
    const contact = id && useContactMap.snapshot()?.[id];
    contact && startEditing(contact);
  }, [startEditing]);
  return <Panel className={disabled ? 'opacity-50 pointer-events-none' : undefined}>
        <ul class="p-0 h-[calc(80vh-200px)] overflow-auto" onChange={onChangeChecked}>
          {contacts?.map(contact => {
        const shared = guests.some(({
          email
        }) => email === contact.email);
        return <li key={contact.id} data-id={contact.id}>
                <label class="px-4 py-1 cursor-pointer flex items-center rounded border border-transparent hover:border-$primary-color">
                  <input class="fb4af615" type="checkbox" defaultChecked={shared} disabled={shared} />
                  <div class="flex-1 mx-3 overflow-hidden">
                    <div class="overflow-hidden overflow-ellipsis">{contact.name}</div>
                    <div class="overflow-hidden overflow-ellipsis font-size-13px opacity-60">{contact.email}</div>
                  </div>
                  <DefaultButton className="!text-current" label={i18n.edit} onClick={onClickEditButton} />
                </label>
              </li>;
      })}
        </ul>
      </Panel>;
});
export const ContactListDialog = memo(forwardRef<HTMLDialogElement, {
  currentUser: {
    readonly email?: string;
  };
  guests: readonly {
    readonly email: string;
  }[];
  maxGuestCount: number;
  invite: (contacts: readonly {
    readonly email: string;
    readonly name: string;
  }[], permissions: DoodlePermissions) => Promise<boolean>;
}>(({
  currentUser,
  guests,
  maxGuestCount,
  invite
}, ref) => {
  useEffect(() => void loadContacts(), []);
  const contactEditDialogRef = useRef<HTMLDialogElement>(null);
  const [editingContact, setEditingContact] = useState<Partial<Contact>>({});
  const registerContact = useCallback(() => {
    if ((useContacts.snapshot()?.length ?? 0) >= 100) {
      return alert(i18n.contacts.maxContactsExceeded);
    }
    setEditingContact({});
    showModal(contactEditDialogRef);
  }, []);
  const startEditing = useCallback((contact: Contact) => {
    setEditingContact({
      ...contact
    });
    showModal(contactEditDialogRef);
  }, []);
  const cancelEdit = useCallback(() => {
    setEditingContact({});
    contactEditDialogRef.current?.close();
  }, []);
  const [selectedContacts, setSelectedContacts] = useState<readonly Contact[]>([]);
  const [busy, setBusy] = useState(false);
  const deleteSelectedContacts = useCallback<() => unknown>(async () => {
    if (selectedContacts.length === 0 || !confirm(i18n.contacts.confirmDelete(selectedContacts))) {
      return;
    }
    try {
      setBusy(true);
      await deleteContacts(selectedContacts.map(contact => contact.id));
      setSelectedContacts(collectSelectedContactIds());
    } finally {
      setBusy(false);
    }
  }, [selectedContacts]);
  const deleteButtonClass = "d1pmgp04";
  const permissionDialogRef = useRef<HTMLDialogElement>(null);
  const openPermissionsDialog = useCallback(() => showModal(permissionDialogRef), []);
  const cancelInvitation = useCallback(() => void permissionDialogRef.current?.close(), []);
  const submitInvitation = useCallback(async (permissions: DoodlePermissions) => {
    if (selectedContacts.length === 0) {
      return;
    }
    if (await invite(selectedContacts, permissions)) {
      setSelectedContacts([]);
      if (permissionDialogRef.current) {
        permissionDialogRef.current.close();
        await new Promise(resolve => permissionDialogRef.current!.addEventListener('close', resolve));
      }
      // FIXME: ref に RefCallback 渡されたら困ってしまう型アサーション。 preact 11 で forwardRef 不要になったら直せる？
      // https://preactjs.com/guide/v11/upgrade-guide/#refs-are-forwarded-by-default
      ;
      (ref as RefObject<HTMLDialogElement>).current?.close();
    }
  }, [invite, selectedContacts, ref]);
  return <Dialog ref={ref} caption={i18n.contacts.caption} header={<>
            <DefaultButton className="!text-current" icon={mdiAccountPlusOutline} label={i18n.contacts.newContact} disabled={busy} onClick={registerContact} />
            <DefaultButton className={deleteButtonClass} icon={mdiDeleteOutline} label={i18n.contacts.deleteContacts(selectedContacts.length)} disabled={busy || selectedContacts.length === 0} onClick={deleteSelectedContacts} />
          </>} onCancel={preventDefault}>
        <div class="flex flex-col gap-4">
          <ContactList disabled={busy} guests={guests} startEditing={startEditing} setSelectedContacts={setSelectedContacts} />
          <PrimaryButton icon={mdiEmailOutline} label={i18n.contacts.inviteMany(selectedContacts.length, maxGuestCount - guests.length)} disabled={busy || selectedContacts.length === 0 || selectedContacts.length > maxGuestCount - guests.length} onClick={openPermissionsDialog} />
          <Dialog ref={contactEditDialogRef} caption={editingContact ? i18n.contacts.editContact : i18n.contacts.newContact} onCancel={preventDefault}>
            <ContactEditForm source={editingContact} close={cancelEdit} />
          </Dialog>
          <Dialog className="!w-400px" ref={permissionDialogRef} caption={i18n.contacts.confirmationCaption} onCancel={preventDefault}>
            <ConfirmationForm currentUser={currentUser} contacts={selectedContacts} onSubmit={submitInvitation} onCancel={cancelInvitation} />
          </Dialog>
        </div>
      </Dialog>;
}));